import { BaseApi } from './base.api.js';
import { ApiError } from '../errors/api-error.js';

export class RiskRegisterApi extends BaseApi {
    constructor(client) {
        super(client, '/risk-register');
    }

    async getStats() {
        try {
            const response = await this.client.get(`${this.basePath}/stats`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getPartyTemplate(params = {}) {
        const response = await this.client.get(`${this.basePath}/party-template`, { params });
        return response.data;
    }

    async getParty(id) {
        const response = await this.client.get(`${this.basePath}/${id}/party`);
        return response.data;
    }

    async syncParty(id, body) {
        const response = await this.client.put(`${this.basePath}/${id}/party`, body);
        return response.data;
    }

    async getControls(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/controls`, { params });
        return response.data;
    }

    async getRelatedTreatments(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/treatments`, { params });
        return response.data;
    }

    async addControls(id, controlIds) {
        const response = await this.client.post(`${this.basePath}/${id}/controls/link`, { controlIds });
        return response.data;
    }

    async removeControl(id, controlId) {
        const response = await this.client.delete(`${this.basePath}/${id}/controls/${controlId}`);
        return response.data;
    }

    async addTreatments(id, treatmentIds) {
        const response = await this.client.post(`${this.basePath}/${id}/treatments/link`, { treatmentIds });
        return response.data;
    }

    async removeTreatment(id, treatmentId) {
        const response = await this.client.delete(`${this.basePath}/${id}/treatments/${treatmentId}`);
        return response.data;
    }

    async getRelatedCauses(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/causes`, { params });
        return response.data;
    }

    async addCauses(id, rootCauseIds) {
        const response = await this.client.post(`${this.basePath}/${id}/causes/link`, { rootCauseIds });
        return response.data;
    }

    async removeCause(id, rootCauseId) {
        const response = await this.client.delete(`${this.basePath}/${id}/causes/${rootCauseId}`);
        return response.data;
    }

    async getRelatedConsequences(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/consequences`, { params });
        return response.data;
    }

    async addConsequences(id, consequenceIds) {
        const response = await this.client.post(`${this.basePath}/${id}/consequences/link`, { consequenceIds });
        return response.data;
    }

    async removeConsequence(id, consequenceId) {
        const response = await this.client.delete(`${this.basePath}/${id}/consequences/${consequenceId}`);
        return response.data;
    }

    async getRelatedAssets(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/assets`, { params });
        return response.data;
    }

    async linkAssets(id, assetIds) {
        const response = await this.client.post(`${this.basePath}/${id}/assets/link`, { assetIds });
        return response.data;
    }

    async unlinkAsset(id, assetId) {
        const response = await this.client.delete(`${this.basePath}/${id}/assets/${assetId}`);
        return response.data;
    }

    async unlinkAssetsBulk(id, assetIds) {
        const response = await this.client.post(`${this.basePath}/${id}/assets/unlink`, { assetIds });
        return response.data;
    }

    async unlinkAssetSource(id, assetId, payload) {
        const response = await this.client.post(`${this.basePath}/${id}/assets/${assetId}/source-unlink`, payload);
        return response.data;
    }

    async linkAssetsByCategory(id, assetCategoryId) {
        const response = await this.client.post(`${this.basePath}/${id}/assets/link-by-category`, { assetCategoryId });
        return response.data;
    }

    async linkAssetsByProcess(id, processId) {
        const response = await this.client.post(`${this.basePath}/${id}/assets/link-by-process`, { processId });
        return response.data;
    }

    async unlinkAssetsByCategory(id, assetCategoryId) {
        const response = await this.client.post(`${this.basePath}/${id}/assets/unlink-by-category`, { assetCategoryId });
        return response.data;
    }

    async unlinkAssetsByProcess(id, processId) {
        const response = await this.client.post(`${this.basePath}/${id}/assets/unlink-by-process`, { processId });
        return response.data;
    }

    async reconcileAssetSources(id) {
        const response = await this.client.post(`${this.basePath}/${id}/assets/reconcile`);
        return response.data;
    }

    async getRelatedBias(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/bias`, { params });
        return response.data;
    }

    async addBias(id, biaIds) {
        const response = await this.client.post(`${this.basePath}/${id}/bias`, { biaIds });
        return response.data;
    }

    async removeBia(id, biaId) {
        const response = await this.client.delete(`${this.basePath}/${id}/bias/${biaId}`);
        return response.data;
    }

    async getRelatedObjectives(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/objectives`, { params });
        return response.data;
    }

    async addObjectives(id, objectiveIds) {
        const response = await this.client.post(`${this.basePath}/${id}/objectives`, { objectiveIds });
        return response.data;
    }

    async removeObjective(id, objectiveId) {
        const response = await this.client.delete(`${this.basePath}/${id}/objectives/${objectiveId}`);
        return response.data;
    }

    async createFilteredReportViewerSession(payload) {
        try {
            const response = await this.client.post(`${this.basePath}/report/viewer-session`, payload, {
                timeout: 180_000,
            });
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }


    async exportFilteredReport(payload) {
        try {
            const response = await this.client.post(`${this.basePath}/report/pdf`, payload, {
                responseType: 'blob',
                timeout: 180_000,
            });
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }
}
